
import { supabase } from "@/integrations/supabase/client";
import { InterviewRequestResponse } from "./types";

export class InterviewRequestsService {
  /**
   * Get an interview request by its link
   */
  static async getInterviewRequestByLink(requestLink: string): Promise<InterviewRequestResponse | null> {
    try {
      console.log('🔍 InterviewRequestsService: Searching for request with link:', requestLink);
      
      const { data: rpcData, error } = await supabase.rpc(
        'find_request_by_link',
        { search_link: requestLink.trim() }
      );
      const data = Array.isArray(rpcData) ? rpcData[0] : rpcData;

      if (error) {
        console.error('❌ InterviewRequestsService: Database error:', error);
        return null;
      }

      console.log('📊 InterviewRequestsService: Raw database response:', {
        hasData: !!data,
        dataType: typeof data,
        allData: data
      });

      if (!data) {
        console.log('❌ InterviewRequestsService: No interview request found for link:', requestLink);
        return null;
      }

      // Let's examine the question field specifically
      console.log('🎯 InterviewRequestsService: Question field analysis:', {
        questionExists: 'question' in data,
        questionValue: data.question,
        questionType: typeof data.question,
        questionLength: data.question ? data.question.length : 0,
        questionIsNull: data.question === null,
        questionIsUndefined: data.question === undefined,
        questionIsEmptyString: data.question === '',
        questionTrimmed: data.question ? data.question.trim() : 'null',
        questionTrimmedLength: data.question ? data.question.trim().length : 0
      });

      // Check if question is actually empty or problematic
      if (!data.question || (typeof data.question === 'string' && data.question.trim().length === 0)) {
        console.error('❌ InterviewRequestsService: Question field is empty or invalid:', {
          originalValue: data.question,
          type: typeof data.question,
          allFields: Object.keys(data)
        });
      }

      console.log('✅ InterviewRequestsService: Returning interview request data');
      return data as InterviewRequestResponse;
    } catch (error) {
      console.error('❌ InterviewRequestsService: Unexpected error:', error);
      return null;
    }
  }

  /**
   * Send an interview request via email
   */
  static async sendInterviewRequest(requestData: any): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .insert([requestData])
        .select();

      if (error) {
        console.error('Error sending interview request:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error sending interview request:', error);
      return { success: false, error: 'Failed to send interview request' };
    }
  }

  /**
   * Get all interview requests (excluding archived by default)
   */
  static async getAllInterviewRequests(): Promise<InterviewRequestResponse[]> {
    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select('*')
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching interview requests:', error);
        return [];
      }

      return data as InterviewRequestResponse[];
    } catch (error) {
      console.error('Error fetching interview requests:', error);
      return [];
    }
  }

  /**
   * Get all archived interview requests
   */
  static async getArchivedInterviewRequests(): Promise<InterviewRequestResponse[]> {
    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select('*')
        .eq('archived', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching archived interview requests:', error);
        return [];
      }

      return data as InterviewRequestResponse[];
    } catch (error) {
      console.error('Unexpected error fetching archived interview requests:', error);
      return [];
    }
  }

  /**
   * Get statistics for interview requests
   */
  static async getInterviewRequestStatistics(): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select('*');

      if (error) {
        console.error('Error fetching statistics:', error);
        return {
          total_sent: 0,
          total_answered: 0,
          total_pending: 0,
          total_expired: 0,
          answer_rate: 0
        };
      }

      const total_sent = data.length;
      const total_answered = data.filter(r => r.status === 'completed').length;
      const total_pending = data.filter(r => r.status === 'pending').length;
      const total_expired = data.filter(r => r.status === 'expired').length;
      const answer_rate = total_sent > 0 ? (total_answered / total_sent) * 100 : 0;

      return {
        total_sent,
        total_answered,
        total_pending,
        total_expired,
        answer_rate: Math.round(answer_rate)
      };
    } catch (error) {
      console.error('Unexpected error fetching statistics:', error);
      return {
        total_sent: 0,
        total_answered: 0,
        total_pending: 0,
        total_expired: 0,
        answer_rate: 0
      };
    }
  }

  /**
   * Archive an interview request
   */
  static async archiveInterviewRequest(requestId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('interview_requests')
        .update({ archived: true })
        .eq('id', requestId);

      if (error) {
        console.error('Error archiving interview request:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Unexpected error archiving interview request:', error);
      return { success: false, error: 'Failed to archive interview request' };
    }
  }

  /**
   * Unarchive an interview request
   */
  static async unarchiveInterviewRequest(requestId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('interview_requests')
        .update({ archived: false })
        .eq('id', requestId);

      if (error) {
        console.error('Error unarchiving interview request:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Unexpected error unarchiving interview request:', error);
      return { success: false, error: 'Failed to unarchive interview request' };
    }
  }

  /**
   * Delete an interview request by ID
   */
  static async deleteInterviewRequest(requestId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('interview_requests')
        .delete()
        .eq('id', requestId);

      if (error) {
        console.error('Error deleting interview request:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting interview request:', error);
      return { success: false, error: 'Failed to delete interview request' };
    }
  }

  /**
   * Delete all interview requests
   */
  static async deleteAllInterviewRequests(): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('interview_requests')
        .delete()
        .not('id', 'is', null);

      if (error) {
        console.error('Error deleting all interview requests:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting all interview requests:', error);
      return { success: false, error: 'Failed to delete all interview requests' };
    }
  }
}
